import React, { useEffect, useId, useMemo, useState } from "react";
import { filterClass } from "../../utils/class-utils";
import type { PhotoAlbumProps } from "../types";
import { useExternalDataStore } from "../../state/external-data-store";
import { useAppStore } from "../../state/app-store";
import {
  getAccordionButtonClass,
  getAccordionCollapseBaseClass,
  getAccordionItemClass,
  getAccordionToggleButtonProps,
  isEasyblocksEditorMode,
  resolveAccordionExpanded,
  resolveAccordionStartOpen,
} from "../utils/accordion-shell";

export function PhotoAlbum(props: PhotoAlbumProps) {
  const {
    paddingYClass = "py-section-md",
    paddingXClass = "no-class",
    bgClass = "no-class",
    slideshowWidthClass = "container-max-xl",
    fixedAlbumId = 0,
    viewMode = "slideshow",
    accordionStartOpen = "false",
  } = props;

  const isStartOpen = resolveAccordionStartOpen(accordionStartOpen);

  const documentId = useAppStore((s) => s.documentId);
  const reactId = useId();
  const instanceId = useMemo(() => `photo-albums-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`, [reactId]);
  const [activeAlbumId, setActiveAlbumId] = useState<string>("");

  const albumsState = useExternalDataStore((s) => s.albumsList[instanceId]);
  const albumDetail = useExternalDataStore((s) => s.albumDetail[instanceId]);
  const ensureAlbumsList = useExternalDataStore((s) => s.ensureAlbumsList);
  const listAlbums = useExternalDataStore((s) => s.listAlbums);
  const getAlbum = useExternalDataStore((s) => s.getAlbum);

  const albums = albumsState?.items ?? [];
  const parsedFixedAlbumId = String(fixedAlbumId ?? "0");
  const normalizedFixedAlbumId = parsedFixedAlbumId !== "0" ? parsedFixedAlbumId : "";
  const isFixedAlbum = normalizedFixedAlbumId.length > 0;
  const hasFixedAlbum = albums.some((album: any) => String(album?.id ?? "") === normalizedFixedAlbumId);
  const effectiveAlbumId = isFixedAlbum ? (hasFixedAlbum ? normalizedFixedAlbumId : activeAlbumId) : activeAlbumId;
  const sectionTitleText = useMemo(() => {
    if (!isFixedAlbum || !hasFixedAlbum) return "Photo Albums";
    const fixedAlbum = albums.find((album: any) => String(album?.id ?? "") === normalizedFixedAlbumId);
    const fixedAlbumName = String(fixedAlbum?.name ?? fixedAlbum?.item_name ?? "Photo Album").trim();
    return fixedAlbumName;
  }, [albums, hasFixedAlbum, isFixedAlbum, normalizedFixedAlbumId]);
  const loadedAlbumId = String(albumDetail?.albumId ?? "");
  const isAlbumInSync = !effectiveAlbumId || loadedAlbumId === effectiveAlbumId;
  const isAlbumLoading = Boolean(albumDetail?.isLoading) || !isAlbumInSync;
  const photos = isAlbumInSync ? albumDetail?.photos ?? [] : [];

  useEffect(() => {
    ensureAlbumsList(instanceId);
    listAlbums(instanceId, 200);
  }, [ensureAlbumsList, listAlbums, instanceId, documentId]);

  useEffect(() => {
    if (!albums.length) return;
    if (isFixedAlbum) {
      const fixedExists = albums.some((album: any) => String(album?.id ?? "") === normalizedFixedAlbumId);
      const fallbackAlbumId = String(albums[0]?.id ?? "");
      setActiveAlbumId(fixedExists ? normalizedFixedAlbumId : fallbackAlbumId);
      return;
    }
    const nextAlbumId = String(albums[0]?.id ?? "");
    if (!nextAlbumId) return;
    setActiveAlbumId((prev) => (prev ? prev : nextAlbumId));
  }, [albums, isFixedAlbum, normalizedFixedAlbumId]);

  useEffect(() => {
    if (!effectiveAlbumId) return;
    getAlbum(instanceId, effectiveAlbumId);
  }, [documentId, effectiveAlbumId, getAlbum, instanceId]);

  const safeAlbumId = useMemo(
    () => String(effectiveAlbumId || "none").replace(/[^a-zA-Z0-9_-]/g, ""),
    [effectiveAlbumId]
  );
  const carouselId = useMemo(
    () => `eb-photo-album-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}-${safeAlbumId}`,
    [reactId, safeAlbumId]
  );

  const accordionId = useMemo(
    () => `photo-album-accordion-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`,
    [reactId]
  );

  const isInEditor = isEasyblocksEditorMode();

  const isAccordionExpanded = resolveAccordionExpanded(isInEditor, isStartOpen);

  const sectionClasses = [bgClass, filterClass(paddingYClass), filterClass(paddingXClass)]
    .filter(Boolean)
    .join(" ");

  const carouselClassNames = ["carousel", "slide"].filter(Boolean).join(" ");
  const getPhotoCaption = (photo: any): string => {
    return String(photo?.item_name ?? "").trim();
  };

  const classes = {
    section: ["section section-photo-album", sectionClasses].filter(Boolean).join(" ").trim(),
    container: "container-xl",
    slideshowWidth: filterClass(slideshowWidthClass) || undefined,
    sectionTitle: "mb-3",
    accordion: "accordion",
    accordionItem: getAccordionItemClass(),
    accordionHeader: "accordion-header",
    accordionButton: (expanded: boolean) => getAccordionButtonClass(isInEditor, expanded),
    accordionCollapse: getAccordionCollapseBaseClass(isInEditor),
    accordionBody: "accordion-body",
    title: "carousel-title",
    carousel: carouselClassNames,
    innerWrapper: "carousel-inner-wrapper",
    controlsRow: "d-flex align-items-center gap-2 mb-3",
    indicators: "carousel-indicators",
    inner: "carousel-inner",
    itemImage: "item-image ratio ratio-4x3",
    img: "d-block mx-auto img-fluid object-fit-contain",
    caption: "carousel-caption d-block",
    controlPrev: "carousel-control-prev",
    controlNext: "carousel-control-next",
    editorNote: "alert alert-info mb-0",
  };

  const renderEditorNote = () => (
    <div className={classes.editorNote} role="status">
      Photo albums are hidden in edit mode. They will appear in the live view.
    </div>
  );

  const renderNoAlbumsNote = () => (
    <div className={classes.editorNote} role="status">
      No photo albums available at this time. This section will not appear in the live view until album data is available.
    </div>
  );

  const renderEditorContent = () =>
    albums.length === 0 && !albumsState?.isLoading ? renderNoAlbumsNote() : renderEditorNote();

  const renderSlideshowContent = () => (
    <>
      {!isFixedAlbum ? (
        <div className={classes.controlsRow}>
          <label htmlFor={`${carouselId}-album-select`} className="small mb-0">
            Album
          </label>
          <select
            id={`${carouselId}-album-select`}
            className="form-select form-select-sm w-100 w-sm-auto"
            value={activeAlbumId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setActiveAlbumId(e.target.value)}
            disabled={Boolean(albumsState?.isLoading) || albums.length === 0}
          >
            {albums.length === 0 ? (
              <option value="">
                {albumsState?.isLoading ? "Loading albums..." : "No albums available for this community"}
              </option>
            ) : (
              albums.map((album: any) => (
                <option key={String(album?.id ?? "")} value={String(album?.id ?? "")}>
                  {String(album?.name ?? album?.item_name ?? "Untitled album")}
                </option>
              ))
            )}
          </select>
        </div>
      ) : null}

      {albumsState?.error && <div className="alert alert-danger">{albumsState.error}</div>}
      {albumDetail?.error && <div className="alert alert-danger">{albumDetail.error}</div>}

      {photos.length === 0 ? (
        <div className="alert alert-secondary mb-0">
          {isAlbumLoading ? "Loading album photos..." : "This album has no photos."}
        </div>
      ) : (
        <div
          key={effectiveAlbumId || "no-album-selected"}
          id={carouselId}
          className={classes.carousel}
          data-bs-ride="false"
          data-bs-wrap="true"
          data-bs-touch="true"
          data-bs-keyboard="true"
        >
          <div className={classes.innerWrapper}>
            <div className={classes.inner}>
              {photos.map((photo: any, idx: number) => {
                const caption = getPhotoCaption(photo);
                return (
                  <div
                    key={String(photo?.id ?? idx)}
                    className={["carousel-item", idx === 0 ? "active" : ""].filter(Boolean).join(" ")}
                  >
                    <div className={classes.itemImage}>
                      <img
                        src={String(photo?.url ?? "")}
                        className={classes.img}
                        alt={String(photo?.item_name ?? "Album photo")}
                      />
                    </div>
                    {caption && <div className={classes.caption}>{caption}</div>}
                  </div>
                );
              })}
            </div>

            {photos.length > 1 && (
              <>
                <button
                  className={classes.controlPrev}
                  type="button"
                  data-bs-target={`#${carouselId}`}
                  data-bs-slide="prev"
                >
                  <i className="fas fa-chevron-left" aria-hidden="true" />
                  <span className="visually-hidden">Previous</span>
                </button>
                <button
                  className={classes.controlNext}
                  type="button"
                  data-bs-target={`#${carouselId}`}
                  data-bs-slide="next"
                >
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                  <span className="visually-hidden">Next</span>
                </button>
              </>
            )}
          </div>

          {photos.length > 0 && (
            <div className={classes.indicators}>
              {photos.map((_, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  data-bs-target={`#${carouselId}`}
                  data-bs-slide-to={idx}
                  className={idx === 0 ? "active" : undefined}
                  aria-current={idx === 0 ? "true" : undefined}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  // In live view, hide the entire section if there are no albums
  if (!isInEditor && albums.length === 0) {
    return null;
  }

  return (
    <section className={classes.section || undefined}>
      <div className={classes.container}>
        {viewMode === "accordion" ? (
          <div className={classes.accordion} id={accordionId}>
            <div className={classes.accordionItem}>
              <h2 className={classes.accordionHeader} id={`${accordionId}-heading`}>
                <button
                  className={classes.accordionButton(isAccordionExpanded)}
                  type="button"
                  aria-expanded={isAccordionExpanded ? "true" : "false"}
                  aria-controls={`${accordionId}-collapse`}
                  {...getAccordionToggleButtonProps(isInEditor, `${accordionId}-collapse`)}
                >
                  <span className="h4 mb-0">{sectionTitleText}</span>
                </button>
              </h2>
              <div
                id={`${accordionId}-collapse`}
                className={[
                  classes.accordionCollapse,
                  isInEditor ? "show" : isStartOpen ? "show" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-labelledby={`${accordionId}-heading`}
              >
                <div className={classes.accordionBody}>
                  <div className={classes.slideshowWidth}>{isInEditor ? renderEditorContent() : renderSlideshowContent()}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className={classes.sectionTitle}>{sectionTitleText}</h2>
            <div className={classes.slideshowWidth}>{isInEditor ? renderEditorNote() : renderSlideshowContent()}</div>
          </>
        )}
      </div>
    </section>
  );
}

export default PhotoAlbum;
